// Rechnungen direkt im Tool schreiben und verwalten. Gespeichert wird in MILKEE (Nummer, MWST, QR-Rechnung, Buchungen).
import { CURRENCIES, INVOICE_STATUS, LANGS, expensesSum, groupTimes, linesNet, posSum, timesSum, type InvExpense, type InvLine, type InvPos, type InvTime, type InvoiceFull, type InvoiceInput, type InvoiceMeta } from "../shared/invoice.js";
import type { MkProject } from "../shared/types.js";
import { ApiError, api } from "./api.js";
import { ic } from "./icons.js";
import { esc, invNr } from "./util.js";

export interface InvoiceHost {
  rerender: () => void;
  toast: (msg: string) => void;
  go: (to: string) => void;
  openModal: (html: string, cls?: string) => void;
  closeModal: () => void;
  confirm: (text: string, label: string, fn: () => void | Promise<void>) => void;
  mhead: (t: string) => string;
  mfoot: (label: string) => string;
  pageHead: (eyebrow: string, title: string, lede: string, acts?: string) => string;
  emptyBox: (title: string, text: string, btn?: string) => string;
  authLost: (e: unknown) => boolean;
  /** «Zahlung erfassen» der Rechnungsliste (Datum, Buchung aufs Bankkonto) */
  paid: (id: number) => void;
  /** Adresse auf die neue Rechnung umstellen, ohne neu zu zeichnen (Fokus bleibt) */
  setRoute: (rid: string) => void;
}
let H: InvoiceHost;
export function initInvoices(host: InvoiceHost) {
  H = host;
}

type Draft = InvoiceInput & { id: number | null; status: string; customerName: string; totals: InvoiceFull["totals"] | null; activities: InvoiceFull["activities"]; times: InvTime[]; expenses: InvExpense[] };
let KEY = "";
let ED: { draft: Draft; dirty: boolean; busy: boolean; err: string } | null = null;
let META: InvoiceMeta | null = null;
let CUSTOMERS: { id: number; name: string }[] | null = null;
let PROJECTS: MkProject[] | null = null;
/** Vorlage für «Duplizieren» bzw. «Neue Rechnung für Kunde» */
let PREFILL: Partial<Draft> | null = null;

const chf = (n: number, cur = "CHF") => `${cur} ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const plusDays = (s: string, n: number) => {
  const d = new Date(`${s}T12:00`);
  d.setDate(d.getDate() + n);
  return iso(d);
};
const dmy = (s: string) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : "");
const failMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Unbekannter Fehler");
const newPos = (p: Partial<InvPos> = {}): InvPos => ({ type: "position", description: "", amount: 1, unit: "Stk.", price: 0, discount: 0, discountType: "percentage", ...p });
const editable = () => !!ED && (ED.draft.id === null || ED.draft.status === "open");

/** Neue Rechnung öffnen, optional vorbelegt (Kunde, Duplikat) */
export function openNewInvoice(prefill: Partial<Draft> = {}) {
  PREFILL = prefill;
  KEY = "";
  H.go("rechnung-neu");
}

function blank(): Draft {
  const today = iso(new Date());
  return {
    id: null,
    status: "open",
    customerId: 0,
    customerName: "",
    projectId: null,
    title: "",
    date: today,
    payableUntil: plusDays(today, 30),
    number: "",
    lang: "de",
    currency: "CHF",
    taxRateId: null,
    discountRate: 0,
    remarksTop: "",
    remarks: "",
    lines: [newPos()],
    timeIds: [],
    entryIds: [],
    groupBy: "none",
    timesOrder: "desc",
    times: [],
    expenses: [],
    totals: null,
    activities: [],
  };
}

async function loadLists() {
  try {
    const [m, c, p] = await Promise.all([META ? Promise.resolve(META) : api.invoiceMeta(), CUSTOMERS ? Promise.resolve({ customers: CUSTOMERS }) : api.milkeeCustomers(), PROJECTS ? Promise.resolve({ projects: PROJECTS }) : api.mkProjects()]);
    META = m;
    CUSTOMERS = c.customers;
    PROJECTS = p.projects ?? [];
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  }
}

async function load(key: string) {
  KEY = key;
  ED = null;
  clearTimeout(SAVE_T);
  SAVE_T = undefined;
  SAVE_ERR = "";
  SAVED_AT = null;
  PREVIEW_V = Date.now();
  await loadLists();
  if (key === "neu") {
    ED = { draft: { ...blank(), ...(PREFILL ?? {}), id: null, status: "open", totals: null, activities: [] }, dirty: !!PREFILL, busy: false, err: "" };
    PREFILL = null;
  } else {
    try {
      const inv = (await api.invoice(Number(key))).invoice;
      ED = { draft: { ...inv }, dirty: false, busy: false, err: "" };
    } catch (e) {
      if (H.authLost(e)) return;
      ED = { draft: blank(), dirty: false, busy: false, err: failMsg(e) };
    }
  }
  H.rerender();
}

/** Nach Statuswechsel von aussen (z. B. «Zahlung erfassen») neu laden */
export function invoiceChanged() {
  if (KEY && KEY !== "neu") void load(KEY);
}

/* ============ Ansicht ============ */

export function vRechnung(key: string): string {
  if (KEY !== key) void load(key);
  const head = (title: string, acts = "") => H.pageHead("Buchhaltung · Rechnungen", title, "Gespeichert wird direkt in MILKEE: Nummer, MWST, QR-Rechnung und Buchungen laufen dort wie gewohnt.", `<a class="btn ghost sm" href="#rechnungen">${ic("left", 14)} Alle Rechnungen</a>${acts}`);
  if (!ED) return `${head(key === "neu" ? "Neue Rechnung" : "Rechnung")}<div class="loading" style="padding:60px 0">Rechnung wird aus MILKEE geladen …</div>`;
  if (ED.err) return `${head("Rechnung")}${H.emptyBox("Rechnung nicht verfügbar", esc(ED.err), `<a class="btn sm" href="#rechnungen">Zur Liste</a>`)}`;
  const d = ED.draft;
  const title = d.id === null ? "Neue Rechnung" : `Rechnung ${esc(invNr(d.number))}`;
  return `${head(title)}
  <div class="inv-wrap">
    <div class="inv-doc ${editable() ? "" : "locked"}">${docHtml(d)}</div>
    <aside class="inv-side"><div id="iv-card">${sideHtml(d)}</div><div id="iv-preview">${previewHtml(d)}</div><div id="iv-act">${actHtml(d)}</div></aside>
  </div>`;
}

function docHtml(d: Draft) {
  const ro = editable() ? "" : "disabled";
  const projects = (PROJECTS ?? []).filter((p) => !d.customerId || p.customerId === d.customerId);
  const cust = `<select class="in" data-iv="customerId" ${ro} required><option value="">Kunde wählen …</option>${(CUSTOMERS ?? []).map((c) => `<option value="${c.id}" ${c.id === d.customerId ? "selected" : ""}>${esc(c.name)}</option>`).join("")}</select>`;
  const proj = `<select class="in" data-iv="projectId" ${ro}><option value="">Kein Projekt</option>${projects.map((p) => `<option value="${p.id}" ${p.id === d.projectId ? "selected" : ""}>${esc(p.name)}</option>`).join("")}</select>`;
  const lines = d.lines.map((l, i) => lineHtml(l, String(i), ro)).join("");
  return `
  <div class="inv-grid">
    <div class="field inv-wide"><label>Kunde</label>${cust}</div>
    <div class="field"><label>Projekt</label>${proj}</div>
    <div class="field"><label>Rechnungsnummer</label><input class="in tnum" data-iv="number" value="${esc(d.number)}" placeholder="${esc(META?.nextNumber ? `${invNr(META.nextNumber)} · vergibt MILKEE` : "vergibt MILKEE automatisch")}" ${ro}></div>
    <div class="field inv-wide"><label>Titel</label><input class="in" data-iv="title" value="${esc(d.title)}" placeholder="z. B. Social Media Content Oktober" ${ro}></div>
    <div class="field"><label>Datum</label><input class="in" type="date" data-iv="date" value="${d.date}" ${ro}></div>
    <div class="field"><label>Zahlbar bis</label><input class="in" type="date" data-iv="payableUntil" value="${d.payableUntil}" ${ro}>${editable() ? `<span class="inv-quick">${[10, 20, 30].map((n) => `<button type="button" data-ivx="due" data-n="${n}">${n} Tage</button>`).join("")}</span>` : ""}</div>
  </div>
  ${ro && !d.remarksTop ? "" : `<div class="field"><label>Einleitungstext</label><textarea class="in inv-ta" data-iv="remarksTop" rows="2" placeholder="z. B. Vielen Dank für den Auftrag. Wir stellen Ihnen folgende Leistungen in Rechnung:" ${ro}>${esc(d.remarksTop)}</textarea></div>`}
  <section class="inv-x">
  <div class="inv-xhead"><h3 class="h3">${ic("list", 15)} Positionen</h3><b class="tnum" id="iv-psum">${chf(linesNet(d.lines), d.currency)}</b></div>
  ${!d.lines.length && (d.times.length || d.expenses.length) ? "" : `<div class="inv-lines">
    <div class="inv-lhead"><span>Beschreibung</span><span class="r">Menge</span><span>Einheit</span><span class="r">Preis</span><span class="r">Rabatt</span><span class="r">Total</span><span></span></div>
    ${lines || `<div class="empty">Noch keine Positionen.</div>`}
  </div>`}
  ${
    editable()
      ? `<div class="inv-add">
    <button class="btn sm" data-ivx="add-pos">${ic("plus", 14)} Position</button>
    <button class="btn ghost sm" data-ivx="add-text">${ic("note", 14)} Text</button>
    <button class="btn ghost sm" data-ivx="add-group">${ic("blocks", 14)} Gruppe</button>
    ${META?.products.length ? `<details class="menu"><summary class="btn ghost sm">${ic("box", 14)} Aus Produkten</summary><div class="menu-list">${META.products.map((p) => `<button data-ivx="add-product" data-id="${p.id}">${esc(p.name)} <span class="muted small" style="margin-left:auto">${chf(p.price)}${p.unit ? `/${esc(p.unit)}` : ""}</span></button>`).join("")}</div></details>` : ""}
  </div>`
      : ""
  }
  </section>
  <div id="iv-extra">${extraHtml(d)}</div>
  ${ro && !d.remarks ? "" : `<div class="field" style="margin-top:18px"><label>Schlusstext</label><textarea class="in inv-ta" data-iv="remarks" rows="2" placeholder="z. B. Zahlbar innert 30 Tagen. Besten Dank!" ${ro}>${esc(d.remarks)}</textarea></div>`}
  <div class="inv-grid inv-opts">
    <div class="field"><label>Rabatt gesamt</label><div class="inv-unit"><input class="in tnum" type="number" step="0.1" min="0" max="100" data-iv="discountRate" value="${d.discountRate || ""}" placeholder="0" ${ro}><span>%</span></div></div>
    <div class="field"><label>MWST</label><select class="in" data-iv="taxRateId" ${ro}><option value="">Keine / gemäss MILKEE</option>${(META?.taxRates ?? []).map((t) => `<option value="${t.id}" ${t.id === d.taxRateId ? "selected" : ""}>${esc(t.name)}</option>`).join("")}</select></div>
    <div class="field"><label>Währung</label><select class="in" data-iv="currency" ${ro}>${CURRENCIES.map((c) => `<option ${c === d.currency ? "selected" : ""}>${c}</option>`).join("")}</select></div>
    <div class="field"><label>Sprache</label><select class="in" data-iv="lang" ${ro}>${LANGS.map((l) => `<option value="${l}" ${l === d.lang ? "selected" : ""}>${{ de: "Deutsch", en: "Englisch", fr: "Französisch", it: "Italienisch" }[l]}</option>`).join("")}</select></div>
  </div>`;
}

const money = (n: number) => n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hm = (min: number) => `${Math.floor(min / 60)}:${String(Math.round(min % 60)).padStart(2, "0")}`;

/** Abschnitte «Zeiten» und «Auslagen» wie in MILKEE: verknüpft statt als Positionen kopiert. Aufbau wie im PDF («Geleistete Stunden»). */
function extraHtml(d: Draft) {
  const ro = !editable();
  const head = (icon: string, title: string, sub: string, sum: number, tools = "") =>
    `<div class="inv-xhead"><h3 class="h3">${ic(icon, 15)} ${title}</h3>${sub ? `<span class="inv-xsub">${sub}</span>` : ""}${tools}<b class="tnum">${chf(sum, d.currency)}</b></div>`;
  const del = (a: string, ids: number[]) => (ro ? "<span></span>" : `<button type="button" class="ibtn sm" data-ivx="${a}" data-ids="${ids.join(",")}" aria-label="Entfernen" title="Von der Rechnung nehmen (bleibt in MILKEE offen)">${ic("x", 13)}</button>`);
  const empty = (icon: string, text: string, btn: string) => `<div class="inv-xempty"><span class="lic">${ic(icon, 17)}</span><span class="min0"><b>${text}</b><small>${d.customerId ? "Holt alles Offene dieses Kunden direkt aus MILKEE." : "Zuerst oben den Kunden wählen."}</small></span>${btn}</div>`;
  const entries = (n: number) => `${n} ${n === 1 ? "Eintrag" : "Einträge"}`;
  let times = "";
  if (d.times.length || !ro) {
    const groups = groupTimes(d.times, d.groupBy, d.timesOrder);
    const rows = groups
      .map((g) => {
        const date = d.groupBy === "project" ? `<span class="muted">${entries(g.ids.length)}</span>` : dmy(g.date);
        const desc = d.groupBy === "date" ? `<b>${entries(g.ids.length)}</b>` : d.groupBy === "project" ? `<b>${esc(g.title || "Projekt")}</b>` : `<b>${esc(g.title || "Arbeit")}</b>${g.sub && g.sub !== g.title ? `<small>${esc(g.sub)}</small>` : ""}`;
        return `<div class="inv-xrow"><span class="tnum">${date}</span><span class="min0">${desc}</span><span class="tnum r">${hm(g.minutes)}</span><span class="tnum r muted">${g.rate ? money(g.rate) : ""}</span><b class="tnum r">${money(g.total)}</b>${del("del-time", g.ids)}</div>`;
      })
      .join("");
    const mins = d.times.reduce((a, t) => a + t.hours * 60 + t.minutes, 0);
    const tools = ro
      ? ""
      : `<span class="inv-xtools">
        <select class="in" data-iv="timesOrder" aria-label="Reihenfolge"><option value="desc" ${d.timesOrder === "desc" ? "selected" : ""}>Neueste zuerst</option><option value="asc" ${d.timesOrder === "asc" ? "selected" : ""}>Älteste zuerst</option></select>
        <select class="in" data-iv="groupBy" aria-label="Gruppieren"><option value="none" ${d.groupBy === "none" ? "selected" : ""}>Nicht gruppieren</option><option value="project" ${d.groupBy === "project" ? "selected" : ""}>Nach Projekt</option><option value="date" ${d.groupBy === "date" ? "selected" : ""}>Nach Datum</option></select>
      </span>`;
    const importBtn = `<button class="btn ${d.times.length ? "ghost" : ""} sm" data-ivx="import-times" ${d.customerId ? "" : "disabled"}>${ic("download", 14)} Offene Zeiten importieren</button>`;
    times = `<section class="inv-x">
      ${head("clock", "Zeiten", d.times.length ? "im PDF als «Geleistete Stunden»" : "", timesSum(d.times), d.times.length ? tools : "")}
      ${
        d.times.length
          ? `<div class="inv-lines inv-xtable">
        <div class="inv-xrow inv-xth"><span>Datum</span><span>Beschreibung</span><span class="r">Zeit</span><span class="r">${esc(d.currency)} / h</span><span class="r">Summe</span><span></span></div>
        ${rows}
        <div class="inv-xrow inv-xfoot"><span></span><span>${entries(d.times.length)}</span><span class="tnum r">${hm(mins)}</span><span></span><b class="tnum r">${money(timesSum(d.times))}</b><span></span></div>
      </div>${ro ? "" : `<div class="inv-add">${importBtn}</div>`}`
          : empty("clock", "Noch keine Zeiten auf dieser Rechnung", importBtn)
      }
    </section>`;
  }
  let exp = "";
  if (d.expenses.length || !ro) {
    const rows = [...d.expenses]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((e) => `<div class="inv-xrow"><span class="tnum">${dmy(e.date)}</span><span class="min0"><b>${esc(e.description || "Auslage")}</b></span><span></span><span></span><b class="tnum r">${money(e.sum)}</b>${del("del-exp", [e.id])}</div>`)
      .join("");
    const importBtn = `<button class="btn ghost sm" data-ivx="import-exp" ${d.customerId ? "" : "disabled"}>${ic("download", 14)} Offene Auslagen importieren</button>`;
    exp = `<section class="inv-x">
      ${head("receipt", "Auslagen", "", expensesSum(d.expenses))}
      ${
        d.expenses.length
          ? `<div class="inv-lines inv-xtable"><div class="inv-xrow inv-xth"><span>Datum</span><span>Beschreibung</span><span></span><span></span><span class="r">Betrag</span><span></span></div>${rows}</div>${ro ? "" : `<div class="inv-add">${importBtn}</div>`}`
          : empty("receipt", "Keine Auslagen", importBtn)
      }
    </section>`;
  }
  return times + exp;
}

function posHtml(p: InvPos, path: string, ro: string, nested = false) {
  return `<div class="inv-line inv-pos ${nested ? "nested" : ""}" data-path="${path}">
    <textarea class="in inv-desc" data-ivl="description" rows="1" placeholder="Beschreibung" ${ro}>${esc(p.description)}</textarea>
    <input class="in tnum r" type="number" step="any" data-ivl="amount" value="${p.amount}" ${ro} aria-label="Menge">
    <input class="in" data-ivl="unit" value="${esc(p.unit)}" list="inv-units" ${ro} aria-label="Einheit">
    <input class="in tnum r" type="number" step="0.05" data-ivl="price" value="${p.price}" ${ro} aria-label="Preis">
    <div class="inv-disc"><input class="in tnum r" type="number" step="any" min="0" data-ivl="discount" value="${p.discount || ""}" placeholder="0" ${ro} aria-label="Rabatt"><button type="button" class="inv-dt" data-ivx="dtype" ${ro} title="Prozent oder Betrag">${p.discountType === "absolute" ? "CHF" : "%"}</button></div>
    <b class="tnum r inv-sum" data-sum="${path}">${posSum(p).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
    ${lineTools(path, ro)}
  </div>`;
}

function lineTools(path: string, ro: string) {
  if (ro) return "<span></span>";
  return `<span class="inv-tools"><button type="button" class="ibtn sm" data-ivx="up" aria-label="Nach oben">${ic("up", 13)}</button><button type="button" class="ibtn sm" data-ivx="down" aria-label="Nach unten">${ic("down", 13)}</button><button type="button" class="ibtn sm" data-ivx="del" aria-label="Entfernen">${ic("trash", 13)}</button></span>`;
}

function lineHtml(l: InvLine, path: string, ro: string) {
  if (l.type === "position") return posHtml(l, path, ro);
  if (l.type === "text")
    return `<div class="inv-line inv-text" data-path="${path}"><textarea class="in inv-desc" data-ivl="text" rows="1" placeholder="Textzeile (ohne Betrag)" ${ro}>${esc(l.text)}</textarea>${lineTools(path, ro)}</div>`;
  const sum = l.positions.reduce((a, p) => a + posSum(p), 0);
  return `<div class="inv-group" data-path="${path}">
    <div class="inv-line inv-ghead"><input class="in" data-ivl="title" value="${esc(l.title)}" placeholder="Titel der Gruppe" ${ro}><b class="tnum r" data-sum="${path}">${sum.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>${lineTools(path, ro)}</div>
    ${l.positions.map((p, j) => posHtml(p, `${path}.${j}`, ro, true)).join("")}
    ${ro ? "" : `<button class="btn ghost sm inv-gadd" data-ivx="add-in-group">${ic("plus", 13)} Position in Gruppe</button>`}
  </div>`;
}

function totals(d: Draft) {
  const net = Math.round((linesNet(d.lines) + timesSum(d.times) + expensesSum(d.expenses)) * 100) / 100;
  const disc = Math.round(net * (d.discountRate || 0)) / 100;
  const rate = META?.taxRates.find((t) => t.id === d.taxRateId)?.rate ?? 0;
  const vat = Math.round((net - disc) * rate) / 100;
  return { net, disc, vat, total: Math.round((net - disc + vat) * 100) / 100, rate };
}

function sideHtml(d: Draft) {
  const t = totals(d);
  const fromMilkee = d.totals && !ED?.dirty;
  const st = d.id === null ? "Neu" : (INVOICE_STATUS[d.status] ?? d.status);
  const stCls = d.status === "paid" ? "ok" : d.status.startsWith("reminder") ? "bad" : d.status === "sent" ? "acc" : "";
  const act = (a: string, label: string, icon: string, cls = "") => `<button class="btn ${cls}" data-ivx="${a}" ${ED?.busy ? "disabled" : ""}>${ic(icon, 15)} ${label}</button>`;
  let actions = "";
  if (editable()) {
    actions = `${d.id !== null ? act("send", "Als versendet markieren", "send", "pri") : ""}
      ${d.id !== null ? act("pdf", "Grosse Vorschau", "eye") : ""}
      ${d.id !== null ? act("delete", "Entwurf löschen", "trash", "ghost dng") : ""}`;
  } else {
    const next: Record<string, [string, string]> = { sent: ["reminder_one", "1. Mahnung"], reminder_one: ["reminder_two", "2. Mahnung"], reminder_two: ["reminder_three", "3. Mahnung"] };
    actions = `${act("pdf", "PDF ansehen", "eye")}
      ${act("mail", "Per Mail senden", "mail")}
      ${d.status !== "paid" ? act("paid", "Zahlung erfassen", "check", "pri") : act("unpaid", "Bezahlt rückgängig", "x", "ghost")}
      ${next[d.status] ? act(`mark:${next[d.status]![0]}`, next[d.status]![1], "bell") : ""}
      ${d.status === "sent" ? act("mark:open", "Zurück auf Entwurf", "edit", "ghost") : ""}`;
  }
  return `<div class="panel inv-sum-card">
    <div class="inv-st"><span class="chip ${stCls}">${esc(st)}</span>${d.times.length ? `<span class="chip">${d.times.length} ${d.times.length === 1 ? "Zeit" : "Zeiten"}</span>` : ""}${editable() ? `<span class="inv-saved" id="iv-saved">${savedText()}</span>` : ""}</div>
    <dl class="inv-tot">
      <dt>Zwischentotal</dt><dd class="tnum" id="iv-net">${chf(fromMilkee && d.totals!.net ? d.totals!.net : t.net, d.currency)}</dd>
      ${(fromMilkee ? d.totals!.discount : t.disc) ? `<dt>Rabatt ${d.discountRate ? `${d.discountRate} %` : ""}</dt><dd class="tnum" id="iv-disc">− ${chf(fromMilkee ? d.totals!.discount : t.disc, d.currency)}</dd>` : ""}
      ${(fromMilkee ? d.totals!.vat : t.vat) ? `<dt>MWST ${t.rate ? `${t.rate} %` : ""}</dt><dd class="tnum" id="iv-vat">${chf(fromMilkee ? d.totals!.vat : t.vat, d.currency)}</dd>` : ""}
      <dt class="inv-big">Total</dt><dd class="tnum inv-big" id="iv-total">${chf(fromMilkee ? d.totals!.total : t.total, d.currency)}</dd>
    </dl>
    <p class="help" style="margin:0 0 12px">${fromMilkee ? "Beträge aus MILKEE." : "Wird gleich in MILKEE gespeichert; MILKEE rechnet Rundung und MWST."}</p>
    <div class="stack" style="gap:8px">${actions}</div>
    ${d.id !== null ? `<button class="btn ghost sm" data-ivx="duplicate" style="margin-top:10px">${ic("copy", 14)} Duplizieren</button>` : ""}
  </div>
  <datalist id="inv-units"><option>Stk.</option><option>h</option><option>Tag</option><option>Pauschal</option><option>Min.</option><option>Monat</option></datalist>`;
}

/* ---------- Live-Vorschau (PDF aus MILKEE) und automatisches Speichern ---------- */

let PREVIEW_V = Date.now();
let SAVE_T: ReturnType<typeof setTimeout> | undefined;
let SAVING = false;
let AGAIN = false;
let SEQ = 0;
let SAVED_AT: Date | null = null;
let SAVE_ERR = "";

const ready = (d: Draft) => !!d.customerId && (d.times.length > 0 || d.expenses.length > 0 || d.lines.some((l) => (l.type === "position" && (l.description.trim() || l.price)) || (l.type === "collection" && l.positions.some((p) => p.description.trim() || p.price))));

function savedText() {
  if (!ED) return "";
  if (SAVE_ERR) return `<span class="bad">${esc(SAVE_ERR)}</span>`;
  if (SAVING || SAVE_T) return "Speichert in MILKEE …";
  if (ED.draft.id === null) return ready(ED.draft) ? "" : "Kunde und eine Position oder Zeit, dann legt MILKEE den Entwurf an";
  if (ED.dirty) return "Änderungen offen";
  return SAVED_AT ? `In MILKEE gespeichert ${SAVED_AT.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })}` : "In MILKEE gespeichert";
}

const actHtml = (d: Draft) =>
  d.activities.length ? `<div class="panel inv-act"><h3 class="h3" style="margin-bottom:8px">Verlauf in MILKEE</h3>${d.activities.slice(0, 12).map((a) => `<div class="inv-actrow"><span class="muted small">${esc(a.date)}</span><span>${esc(a.message)}</span></div>`).join("")}</div>` : "";

function previewHtml(d: Draft) {
  if (d.id === null)
    return `<div class="panel inv-prev empty-prev"><span class="lic">${ic("eye", 18)}</span><b>Live-Vorschau</b><small>Sobald Kunde und eine Position eingetragen sind, legt MILKEE den Entwurf an und zeigt hier die Rechnung genau so, wie sie als PDF rausgeht.</small></div>`;
  const url = `/api/milkee/documents/invoice/${d.id}/pdf`;
  return `<div class="panel inv-prev"><div class="inv-prev-head"><b>Vorschau aus MILKEE</b><a class="btn sm" href="${url}" download="Rechnung ${esc(d.number)}.pdf">${ic("download", 14)} PDF</a></div><iframe id="iv-frame" src="${url}?v=${PREVIEW_V}#toolbar=0&navpanes=0&view=FitH" title="Rechnung als PDF"></iframe></div>`;
}

/** nur die Seitenspalte nachführen (das Formular bleibt, Fokus und Cursor auch) */
function refreshSide(reloadPdf = false) {
  if (!ED) return;
  const card = document.getElementById("iv-card");
  if (card) card.innerHTML = sideHtml(ED.draft);
  const act = document.getElementById("iv-act");
  if (act) act.innerHTML = actHtml(ED.draft);
  const h1 = document.querySelector("#view h1");
  if (h1 && ED.draft.id !== null) h1.textContent = `Rechnung ${invNr(ED.draft.number)}`;
  const prev = document.getElementById("iv-preview");
  if (!prev) return;
  const frame = document.getElementById("iv-frame") as HTMLIFrameElement | null;
  if (!frame || ED.draft.id === null) prev.innerHTML = previewHtml(ED.draft);
  else if (reloadPdf) frame.src = `/api/milkee/documents/invoice/${ED.draft.id}/pdf?v=${PREVIEW_V}#toolbar=0&navpanes=0&view=FitH`;
}

function scheduleSave() {
  if (!ED || !editable()) return;
  clearTimeout(SAVE_T);
  SAVE_T = setTimeout(() => {
    SAVE_T = undefined;
    void persist(true);
  }, 1600);
  const s = document.getElementById("iv-saved");
  if (s) s.innerHTML = savedText();
}

function buildBody(d: Draft): InvoiceInput {
  return {
    customerId: d.customerId,
    projectId: d.projectId,
    title: d.title.trim(),
    date: d.date,
    payableUntil: d.payableUntil,
    number: d.number.trim(),
    lang: d.lang,
    currency: d.currency,
    taxRateId: d.taxRateId,
    discountRate: d.discountRate,
    remarksTop: d.remarksTop,
    remarks: d.remarks,
    // leere Positionen nicht mitschicken, wenn Zeiten oder Auslagen drauf sind
    lines: d.times.length || d.expenses.length ? d.lines.filter((l) => !(l.type === "position" && !l.description.trim() && !l.price)) : d.lines,
    timeIds: d.times.map((t) => t.id),
    entryIds: d.expenses.map((e) => e.id),
    groupBy: d.groupBy,
    timesOrder: d.timesOrder,
  };
}

/** In MILKEE speichern. Leise (beim Tippen): nur Seitenspalte und Vorschau nachführen. */
async function persist(quiet: boolean): Promise<boolean> {
  if (!ED || !editable()) return false;
  if (SAVING) {
    AGAIN = true;
    return false;
  }
  const d = ED.draft;
  if (!ready(d)) {
    if (!quiet) H.toast(d.customerId ? "Mindestens eine Position mit Text oder Preis, eine Zeit oder eine Auslage." : "Bitte zuerst den Kunden wählen.");
    refreshSide();
    return false;
  }
  SAVING = true;
  SAVE_ERR = "";
  const seq = SEQ;
  refreshSide();
  try {
    const body = buildBody(d);
    const inv = d.id === null ? (await api.createInvoice(body)).invoice : (await api.saveInvoice(d.id, body)).invoice;
    const wasNew = d.id === null;
    if (!ED) return false;
    // Eingaben bleiben wie getippt; von MILKEE kommen Nummer, Status, Beträge und Verlauf
    Object.assign(ED.draft, { id: inv.id, number: inv.number, status: inv.status, totals: inv.totals, activities: inv.activities, customerName: inv.customerName });
    if (seq === SEQ) {
      ED.dirty = false;
      // was MILKEE wirklich verknüpft hat
      const lost = body.timeIds.length - inv.times.length + (body.entryIds.length - inv.expenses.length);
      ED.draft.times = inv.times;
      ED.draft.expenses = inv.expenses;
      const x = document.getElementById("iv-extra");
      if (x) x.innerHTML = extraHtml(ED.draft);
      if (lost > 0) H.toast(`MILKEE hat ${lost} Zeit(en)/Auslage(n) nicht übernommen (schon auf einer anderen Rechnung?).`);
    }
    SAVED_AT = new Date();
    META = null;
    // die von MILKEE vergebene Nummer ins Feld (ausser du tippst gerade darin)
    const nrIn = document.querySelector<HTMLInputElement>('[data-iv="number"]');
    if (nrIn && document.activeElement !== nrIn && !nrIn.value.trim()) nrIn.value = inv.number;
    if (wasNew) {
      KEY = String(inv.id);
      H.setRoute(KEY);
      if (!quiet) H.toast(`Rechnung ${invNr(inv.number)} in MILKEE angelegt`);
    } else if (!quiet) H.toast("In MILKEE gespeichert");
    PREVIEW_V = Date.now();
    return true;
  } catch (e) {
    if (H.authLost(e)) return false;
    SAVE_ERR = `Nicht gespeichert: ${failMsg(e)}`;
    if (!quiet) H.toast(failMsg(e));
    return false;
  } finally {
    SAVING = false;
    refreshSide(true);
    if (AGAIN) {
      AGAIN = false;
      scheduleSave();
    }
  }
}

/** Totale ohne Neuaufbau nachführen (Fokus bleibt im Feld) */
function refreshTotals() {
  if (!ED) return;
  const d = ED.draft;
  const t = totals(d);
  const set = (id: string, v: string) => {
    const el = document.getElementById(id);
    if (el) el.textContent = v;
  };
  set("iv-net", chf(t.net, d.currency));
  set("iv-psum", chf(linesNet(d.lines), d.currency));
  set("iv-disc", `− ${chf(t.disc, d.currency)}`);
  set("iv-vat", chf(t.vat, d.currency));
  set("iv-total", chf(t.total, d.currency));
  const fmt = (n: number) => n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  d.lines.forEach((l, i) => {
    const el = document.querySelector(`[data-sum="${i}"]`);
    if (el && l.type === "position") el.textContent = fmt(posSum(l));
    if (el && l.type === "collection") el.textContent = fmt(l.positions.reduce((a, p) => a + posSum(p), 0));
    if (l.type === "collection")
      l.positions.forEach((p, j) => {
        const e2 = document.querySelector(`[data-sum="${i}.${j}"]`);
        if (e2) e2.textContent = fmt(posSum(p));
      });
  });
}

/* ============ Bearbeiten ============ */

/** Zeile anhand des Pfads («3» oder «3.1» für Position in Gruppe) */
function at(path: string): { list: InvLine[] | InvPos[]; i: number } | null {
  if (!ED) return null;
  const [a, b] = path.split(".").map(Number);
  if (b === undefined) return { list: ED.draft.lines, i: a! };
  const g = ED.draft.lines[a!];
  return g && g.type === "collection" ? { list: g.positions, i: b } : null;
}

function touch(rerender = false) {
  if (!ED) return;
  ED.dirty = true;
  SEQ++;
  if (rerender) H.rerender();
  else refreshTotals();
  scheduleSave();
}

document.addEventListener("input", (e) => {
  const el = e.target as HTMLInputElement;
  if (!ED || !editable()) return;
  if (el.dataset.iv) {
    const k = el.dataset.iv as keyof InvoiceInput;
    const d = ED.draft as unknown as Record<string, unknown>;
    if (k === "customerId" || k === "projectId" || k === "taxRateId") d[k] = el.value ? Number(el.value) : k === "customerId" ? 0 : null;
    else if (k === "discountRate") d[k] = Number.parseFloat(el.value) || 0;
    else d[k] = el.value;
    if (k === "customerId") {
      ED.draft.customerName = CUSTOMERS?.find((c) => c.id === ED!.draft.customerId)?.name ?? "";
      ED.draft.projectId = null;
      // Zeiten und Auslagen gehören zum Kunden
      ED.draft.times = [];
      ED.draft.expenses = [];
      return touch(true);
    }
    return touch(k === "projectId" || k === "taxRateId" || k === "currency" || k === "groupBy" || k === "timesOrder");
  }
  const key = el.dataset.ivl;
  const row = el.closest<HTMLElement>("[data-path]");
  if (!key || !row) return;
  const loc = at(row.dataset.path!);
  if (!loc) return;
  const line = loc.list[loc.i] as unknown as Record<string, unknown>;
  line[key] = key === "amount" || key === "price" || key === "discount" ? Number.parseFloat(el.value) || 0 : el.value;
  if (el.tagName === "TEXTAREA") grow(el as unknown as HTMLTextAreaElement);
  touch();
});

const grow = (t: HTMLTextAreaElement) => {
  t.style.height = "auto";
  t.style.height = `${t.scrollHeight + 2}px`;
};

document.addEventListener("click", (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>("[data-ivx]");
  if (!el || !ED || !H) return;
  e.preventDefault();
  const a = el.dataset.ivx!;
  const d = ED.draft;
  const row = el.closest<HTMLElement>("[data-path]");
  const loc = row ? at(row.dataset.path!) : null;
  el.closest("details.menu")?.removeAttribute("open");
  switch (a) {
    case "due":
      d.payableUntil = plusDays(d.date, Number(el.dataset.n));
      return touch(true);
    case "add-pos":
      d.lines.push(newPos());
      return touch(true);
    case "add-text":
      d.lines.push({ type: "text", text: "" });
      return touch(true);
    case "add-group":
      d.lines.push({ type: "collection", title: "", positions: [newPos()] });
      return touch(true);
    case "add-in-group": {
      const g = loc && (loc.list[loc.i] as InvLine);
      if (g && g.type === "collection") g.positions.push(newPos());
      return touch(true);
    }
    case "add-product": {
      const p = META?.products.find((x) => x.id === Number(el.dataset.id));
      if (!p) return;
      // eine leere erste Position ersetzen
      const first = d.lines[0];
      const pos = newPos({ description: p.description ? `${p.name}\n${p.description}` : p.name, price: p.price, unit: p.unit || "Stk." });
      if (d.lines.length === 1 && first?.type === "position" && !first.description && !first.price) d.lines[0] = pos;
      else d.lines.push(pos);
      return touch(true);
    }
    case "dtype": {
      const p = loc && (loc.list[loc.i] as InvPos);
      if (p && p.type === "position") p.discountType = p.discountType === "percentage" ? "absolute" : "percentage";
      return touch(true);
    }
    case "up":
    case "down": {
      if (!loc) return;
      const j = a === "up" ? loc.i - 1 : loc.i + 1;
      if (j < 0 || j >= loc.list.length) return;
      const list = loc.list as unknown[];
      [list[loc.i], list[j]] = [list[j], list[loc.i]];
      return touch(true);
    }
    case "del":
      if (!loc) return;
      (loc.list as unknown[]).splice(loc.i, 1);
      return touch(true);
    case "import-times":
    case "import-exp":
      return void importOpen(a === "import-times" ? "times" : "expenses");
    case "del-time":
    case "del-exp": {
      const ids = (el.dataset.ids ?? "").split(",").map(Number);
      if (a === "del-time") d.times = d.times.filter((t) => !ids.includes(t.id));
      else d.expenses = d.expenses.filter((x) => !ids.includes(x.id));
      return touch(true);
    }
    case "save":
      return void save();
    case "pdf":
      return void pdf();
    case "send":
      return void (async () => {
        if (ED?.dirty && !(await save())) return;
        H.confirm("Die Rechnung wird in MILKEE als «versendet» markiert und lässt sich danach nicht mehr bearbeiten. Je nach Einstellung bucht MILKEE die Forderung.", "Als versendet markieren", () => mark("sent"));
      })();
    case "delete":
      return H.confirm("Diesen Entwurf in MILKEE endgültig löschen?", "Löschen", async () => {
        try {
          await api.deleteInvoice(d.id!);
          H.toast("Entwurf gelöscht");
          KEY = "";
          H.go("rechnungen");
        } catch (err) {
          if (!H.authLost(err)) H.toast(failMsg(err));
        }
      });
    case "paid":
      return H.paid(d.id!);
    case "unpaid":
      return H.confirm("Die Zahlung wird in MILKEE rückgängig gemacht; die Buchung dazu wird gelöscht.", "Rückgängig", () => mark("unpaid"));
    case "mail":
      return mail();
    case "duplicate":
      return openNewInvoice({ ...d, id: null, status: "open", number: "", date: iso(new Date()), payableUntil: plusDays(iso(new Date()), 30), timeIds: [], entryIds: [], times: [], expenses: [], totals: null, activities: [], lines: JSON.parse(JSON.stringify(d.lines)) as InvLine[] });
    default:
      if (a.startsWith("mark:")) {
        const s = a.slice(5);
        const label = s === "open" ? "wieder als Entwurf" : (INVOICE_STATUS[s] ?? s);
        return H.confirm(`Die Rechnung wird in MILKEE ${s === "open" ? "zurück auf «Entwurf» gesetzt" : `als «${label}» markiert`}.`, "Bestätigen", () => mark(s));
      }
  }
});

const save = () => {
  clearTimeout(SAVE_T);
  SAVE_T = undefined;
  return persist(false);
};

async function mark(status: string) {
  if (!ED?.draft.id) return;
  try {
    await api.mkMark("invoice", ED.draft.id, status);
    H.toast("In MILKEE geändert");
    await load(String(ED.draft.id));
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  }
}

async function pdf() {
  if (!ED) return;
  if (editable() && ED.dirty && !(await save())) return;
  const id = ED.draft.id;
  if (!id) return;
  const url = `/api/milkee/documents/invoice/${id}/pdf`;
  H.openModal(`${H.mhead(`Rechnung ${esc(invNr(ED.draft.number))}`)}<iframe class="inv-pdf" src="${url}" title="PDF-Vorschau"></iframe><div class="modal-foot"><a class="btn" href="${url}" download="Rechnung ${esc(ED.draft.number)}.pdf">${ic("download", 15)} PDF herunterladen</a><button class="btn pri" data-a="close">Schliessen</button></div>`, "xwide");
}

function mail() {
  if (!ED?.draft.id) return;
  const d = ED.draft;
  // PDF herunterladen und Mail mit Betreff und Text öffnen; das PDF hängst du an
  const a = document.createElement("a");
  a.href = `/api/milkee/documents/invoice/${d.id}/pdf`;
  a.download = `Rechnung ${d.number}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  const total = d.totals ? chf(d.totals.total, d.currency) : "";
  const subject = `Rechnung ${d.number}${d.title ? ` – ${d.title}` : ""}`;
  const body = `Guten Tag\n\nIm Anhang erhalten Sie die Rechnung ${d.number}${d.title ? ` (${d.title})` : ""} über ${total}, zahlbar bis ${dmy(d.payableUntil)}.\n\nVielen Dank und freundliche Grüsse\n`;
  setTimeout(() => (location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`), 600);
  H.toast("PDF heruntergeladen, Mail wird geöffnet: PDF anhängen und senden.");
}

/* ---------- Offene Zeiten / Auslagen importieren (wie in MILKEE) ---------- */

async function importOpen(kind: "times" | "expenses") {
  if (!ED?.draft.customerId) return H.toast("Bitte zuerst den Kunden wählen.");
  const d = ED.draft;
  const label = kind === "times" ? "Zeiten" : "Auslagen";
  H.toast(`Offene ${label} werden aus MILKEE geladen …`);
  try {
    const src = await api.invoiceSources(d.customerId);
    if (!ED || ED.draft !== d) return;
    let added = 0;
    if (kind === "times") {
      const have = new Set(d.times.map((t) => t.id));
      const add = src.times.filter((t) => !have.has(t.id));
      d.times.push(...add);
      added = add.length;
    } else {
      const have = new Set(d.expenses.map((e) => e.id));
      const add = src.expenses.filter((e) => !have.has(e.id));
      d.expenses.push(...add);
      added = add.length;
    }
    if (!added) return H.toast(kind === "times" ? "Keine offenen, verrechenbaren Zeiten (Stundenprojekte) für diesen Kunden." : "Keine offenen, weiterverrechenbaren Auslagen für diesen Kunden.");
    // eine leere erste Position fällt weg, wie in MILKEE
    const first = d.lines[0];
    if (d.lines.length === 1 && first?.type === "position" && !first.description.trim() && !first.price) d.lines = [];
    H.toast(`${added} ${added === 1 ? (kind === "times" ? "Zeit" : "Auslage") : label} übernommen`);
    touch(true);
  } catch (e) {
    if (!H.authLost(e)) H.toast(failMsg(e));
  }
}

/** Neu gezeichnet: Textfelder an ihren Inhalt anpassen */
export function afterRenderInvoice() {
  // nach dem Layout messen, sonst stimmt die Höhe noch nicht
  requestAnimationFrame(() => document.querySelectorAll<HTMLTextAreaElement>(".inv-doc textarea").forEach(grow));
}

